import React, { useState } from 'react';
import { useCommunityAnalytics } from '../hooks/useCommunityAnalytics';

interface IPosthogAnalyticsCardProps {
	communityId: string;
	posthogApiKey?: string | null;
	onRefetch: () => void;
}

const PosthogAnalyticsCard: React.FC<IPosthogAnalyticsCardProps> = ({
	communityId,
	posthogApiKey,
	onRefetch,
}) => {
	const { loading, error, result, saveAnalyticsKey } = useCommunityAnalytics();
	const [apiKey, setApiKey] = useState('');
	const [validationError, setValidationError] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);

	const displayKey = posthogApiKey || result?.posthogApiKey || '';
	const isCompleted = !!displayKey;

	const handleChange = (value: string) => {
		setApiKey(value);
		if (validationError) setValidationError(null);
	};

	const handleSave = async () => {
		if (!apiKey.trim()) {
			setValidationError('PostHog API key is required.');
			return;
		}

		const res = await saveAnalyticsKey(communityId, apiKey);
		if (res) onRefetch();
	};

	const handleCopy = () => {
		if (!displayKey) return;
		navigator.clipboard.writeText(displayKey).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		});
	};

	if (isCompleted) {
		return (
			<div className="np-os-card card border-0 shadow-sm mt-3">
				<div className="card-body p-4">
					<div className="d-flex align-items-center mb-3">
						<div className="np-os-icon-wrap mr-3">
							<i className="fas fa-chart-line fa-lg text-success"></i>
						</div>
						<div>
							<h6 className="mb-0 font-weight-bold">PostHog Analytics</h6>
							<small className="text-muted">Analytics is configured and locked for this community.</small>
						</div>
					</div>

					<div className="np-os-success alert alert-success py-3 mb-0">
						<div className="d-flex align-items-center mb-2">
							<i className="fas fa-check-circle mr-2"></i>
							<strong>PostHog Analytics is configured.</strong>
						</div>
						<label className="small text-muted mb-1 d-block">API Key</label>
						<div className="input-group input-group-sm">
							<input
								type="text"
								className="form-control np-os-appid-input"
								readOnly
								value={displayKey}
							/>
							<div className="input-group-append">
								<button
									className="btn btn-outline-secondary"
									type="button"
									onClick={handleCopy}
								>
									<i className={`fas ${copied ? 'fa-check text-success' : 'fa-copy'} mr-1`}></i>
									{copied ? 'Copied' : 'Copy'}
								</button>
							</div>
						</div>
						{result?.status !== undefined && (
							<small className="text-muted mt-1 d-block">
								Status code: <code>{result.status}</code>
							</small>
						)}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="np-os-card card border-0 shadow-sm mt-3">
			<div className="card-body p-4">
				<div className="d-flex align-items-center mb-3">
					<div className="np-os-icon-wrap mr-3">
						<i className="fas fa-chart-line fa-lg text-primary"></i>
					</div>
					<div>
						<h6 className="mb-0 font-weight-bold">PostHog Analytics</h6>
						<small className="text-muted">
							Enter the PostHog API key for this community. No custom domain is required.
						</small>
					</div>
				</div>

				{error && (
					<div className="alert alert-danger py-2 mb-3">
						<i className="fas fa-exclamation-circle mr-1"></i>
						{error}
					</div>
				)}

				<div className="form-group">
					<label htmlFor="posthogApiKey">PostHog API Key <span className="text-danger">*</span></label>
					<input
						id="posthogApiKey"
						type="text"
						className={`form-control ${validationError ? 'is-invalid' : ''}`}
						placeholder="Enter PostHog API key"
						value={apiKey}
						onChange={e => handleChange(e.target.value)}
						disabled={loading}
					/>
					{validationError && <div className="invalid-feedback">{validationError}</div>}
				</div>

				<button
					type="button"
					className="np-os-btn btn btn-primary btn-lg px-5"
					onClick={handleSave}
					disabled={loading}
				>
					{loading ? (
						<>
							<span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
							Saving…
						</>
					) : (
						<>
							<i className="fas fa-save mr-2"></i>
							Save PostHog Analytics
						</>
					)}
				</button>
			</div>
		</div>
	);
};

export default PosthogAnalyticsCard;
